import React, { useState } from "react";
import { useQuery, queryCache } from "react-query";
import {
  EuiBasicTable,
  EuiButton,
  EuiCallOut,
  EuiConfirmModal,
  EuiFieldNumber,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiSpacer,
} from "@elastic/eui";
import { addHold, getHolds, removeHold } from "../../api/appReleases";
import { getErrorMessage, RELEASE_QUERY_KEY } from "./helpers";

const HoldsTab = () => {
  const [outletId, setOutletId] = useState("");
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [holdToRemove, setHoldToRemove] = useState(null);

  const { data: holds, isLoading, error } = useQuery(
    [RELEASE_QUERY_KEY, "holds"],
    getHolds,
    { retry: false },
  );

  const isValid = Number(outletId) > 0 && reason.trim() && until;

  const runChange = async (change) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await change();
      queryCache.invalidateQueries(RELEASE_QUERY_KEY);
      return true;
    } catch (err) {
      setSaveError(getErrorMessage(err));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const onAdd = async () => {
    const saved = await runChange(() =>
      addHold({ outlet_id: Number(outletId), reason: reason.trim(), until }),
    );
    if (saved) {
      setOutletId("");
      setReason("");
      setUntil("");
    }
  };

  const onRemove = async () => {
    await runChange(() => removeHold(holdToRemove.id));
    setHoldToRemove(null);
  };

  const columns = [
    {
      name: "Outlet",
      render: (hold) => hold.outlet_name || `Outlet ${hold.outlet_id}`,
    },
    { field: "reason", name: "Reason" },
    { field: "until", name: "Until" },
    { field: "created_by", name: "Added by" },
    {
      name: "",
      width: "100px",
      render: (hold) => (
        <EuiButton size="s" color="danger" onClick={() => setHoldToRemove(hold)}>
          Remove
        </EuiButton>
      ),
    },
  ];

  return (
    <>
      <EuiFlexGroup alignItems="flexEnd" gutterSize="s" wrap>
        <EuiFlexItem grow={false}>
          <EuiFormRow label="Outlet ID">
            <EuiFieldNumber
              min={1}
              value={outletId}
              onChange={(event) => setOutletId(event.target.value)}
            />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiFormRow label="Reason" fullWidth>
            <EuiFieldText
              fullWidth
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFormRow label="Until">
            <EuiFieldText
              type="date"
              aria-label="Until"
              value={until}
              onChange={(event) => setUntil(event.target.value)}
            />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFormRow hasEmptyLabelSpace>
            <EuiButton
              fill
              isDisabled={!isValid}
              isLoading={isSaving}
              onClick={onAdd}
            >
              Add hold
            </EuiButton>
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="m" />
      {saveError || error ? (
        <>
          <EuiCallOut
            size="s"
            color="danger"
            iconType="alert"
            title={saveError || getErrorMessage(error)}
          />
          <EuiSpacer size="s" />
        </>
      ) : null}
      <EuiBasicTable
        items={holds || []}
        columns={columns}
        loading={isLoading}
        noItemsMessage="No outlets on hold"
      />
      {holdToRemove ? (
        <EuiConfirmModal
          title="Remove hold"
          onCancel={() => setHoldToRemove(null)}
          onConfirm={onRemove}
          cancelButtonText="Cancel"
          confirmButtonText="Remove hold"
          buttonColor="danger"
          isLoading={isSaving}
        >
          {holdToRemove.outlet_name || `Outlet ${holdToRemove.outlet_id}`} can
          be picked for a rollout again.
        </EuiConfirmModal>
      ) : null}
    </>
  );
};

export default HoldsTab;
